import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Collapse,
  Typography,
  List,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import paperclipIcon from "@iconify/icons-mdi/paperclip";
import { InlineIcon } from "@iconify/react";
import axios from "axios";
import { OcDropzoneArea, OcFileListItem, type FileRejection } from "oc/components";
import {
  useOcApi,
  useOcAssets,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcSnackbar,
} from "oc/hooks";

interface MyHrDropzoneEditorProps {
  type: string;
  filename?: string;
}

export const MyHrDropzoneEditor: React.FC<MyHrDropzoneEditorProps> = ({
  type,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();
  const { uploadUrl } = useOcApi();
  const { data, refetch, deleteAsset } = useOcAssets({ type });

  const { setOpenMessage } = useOcSnackbar();

  const [newChangeFileDocument, setNewChangeFileDocument] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [, /*isUpload,*/ setIsUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [axiosError, setAxiosError] = useState(false);

  let { employeeCode } = useOcCurrentUserState();

  const handleUpload = async (file: File) => {
    setIsUpload(true);
    const formData = new FormData();
    formData.append("employeeCode", "");
    formData.append("type", type);
    formData.append("owner", employeeCode);
    formData.append("file", file);
    try {
      const config = {
        onUploadProgress: function (
          progressEvent: import("axios").AxiosProgressEvent,
        ) {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(percentCompleted);
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      if (file) {
        await axios.post(uploadUrl, formData, config);
        setIsUpload(false);
        setUploadProgress(100);
        refetch();
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response &&
        error.response.data &&
        error.response.data.errors !== undefined
      ) {
        setAxiosError(true);
      }
    }
  };

  const removeUpload = (item: { _id: string }) => {
    const { _id } = item;
    deleteAsset({ variables: { _id } });
  };

  const handleDropzoneChange = (file: File | File[]) => {
    let selectedFile: File | null = null;
    if (Array.isArray(file)) {
      selectedFile = file.length > 0 ? file[0] : null;
    } else {
      selectedFile = file;
    }
    if (!selectedFile) {
      return;
    }
    setFile(selectedFile);
    handleUpload(selectedFile);
  };

  const handleDropRejected = (fileRejections: FileRejection[]) => {
    const errorCode = fileRejections[0]?.errors[0]?.code;

    let message;
    if (errorCode === 'file-too-large') {
      message = t("OC_FILE_UPLOAD_SIZE_FAILED");
    } else if (errorCode === 'file-invalid-type') {
      message = t("OC_UPLOAD_FAILED");
    } else {
      message = t("OC_UPLOAD_FAILED");
    }

    setOpenMessage({
      type: "error",
      message,
    });
  };

  const removeAssetTag = (filename: string) => {
    let filenameArray = filename.split("/");
    return filenameArray[filenameArray.length - 1];
  };

  return (
    <>
      {newChangeFileDocument ? null : (
        <Button
          id="myhr-dropzone-upload-document"
          variant="outlined"
          fullWidth
          startIcon={<InlineIcon icon={paperclipIcon} />}
          style={{
            background: theme.palette.gray30.main,
            border: "none",
            color: theme.palette.darkBase.main,
          }}
          onClick={() => setNewChangeFileDocument(true)}
        >
          <Typography
            variant="body2"
            color={theme.palette.darkBase.main}
            flexGrow={1}
          >
            {t("MY_HR_UPLOAD_FILE")}
          </Typography>
        </Button>
      )}
      <Collapse
        in={newChangeFileDocument}
        timeout="auto"
        id="myhr-dropzone-area"
      >
        <OcDropzoneArea
          dropzoneText={t("OC_DRAG_AND_DROP_FILE")}
          acceptedFiles={{
            "application/*": [".pdf"],
            "image/*": [".png", ".gif", ".jpeg", ".jpg"],
          }}
          onChange={handleDropzoneChange}
          onDropRejected={handleDropRejected}
          height="82px"
          maxSize={9437184}
        />
      </Collapse>
      <Box height="32px" />
      {uploadProgress === 100 ? null : (
        <Collapse in={file ? true : false}>
          <List>
            {file && (
              <OcFileListItem
                filename={file.name}
                uploadProgress={uploadProgress}
                onDelete={() => removeUpload({ _id: file.name })}
                isMobile={isMobile}
              />
            )}
          </List>
        </Collapse>
      )}
      <Collapse in={data.length > 0}>
        <List>
          {data.map((item: any, index: number) => (
            <OcFileListItem
              key={`${item}-${index}`}
              filename={removeAssetTag(item.filename)}
              error={axiosError ? t("OC_UPLOAD_FAILED") : undefined}
              onDelete={() => removeUpload(item)}
              isMobile={isMobile}
            />
          ))}
        </List>
      </Collapse>
      <Collapse in={newChangeFileDocument}>
        <Typography
          style={{
            fontSize: "12px",
            letterSpacing: "0.4px",
            lineHeight: "16px",
            marginBottom: "32px",
            color: "#5E6366",
          }}
        >
          A feltöltéssel hozzájárulok ahhoz, hogy az ügyintéző a feltöltött
          dokumentumban lévő adataimat megtekinthesse. A változott adat(ok)
          jóváhagyását követően a feltöltött dokumentum automatikusan törlésre
          kerül.
        </Typography>
      </Collapse>
    </>
  );
};

export default MyHrDropzoneEditor;
